import { Link } from 'react-router-dom';
import { FiPlay, FiLock, FiEye } from 'react-icons/fi';
import { Stream } from '@/types';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface StreamCardProps {
  stream: Stream;
  compact?: boolean;
}

const StreamCard = ({ stream, compact = false }: StreamCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Format view count
  const formatViewCount = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  return (
    <Link 
      to={`/stream/${stream.id}`}
      className="block"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.div 
        className={`relative overflow-hidden ${compact ? 'rounded-xl' : 'card card-hover shadow-3d'}`}
        whileHover={{ y: -8 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        {/* Thumbnail */}
        <div className={`relative ${compact ? 'aspect-video' : 'aspect-video mb-4 rounded-t-xl overflow-hidden'}`}>
          <div className="absolute inset-0 bg-dark-700 animate-pulse"></div>
          {stream.thumbnailUrl && (
            <img 
              src={stream.thumbnailUrl || '/images/stream-placeholder.jpg'}
              alt={stream.title}
              className="w-full h-full object-cover rounded-t-xl"
              onError={(e) => {
                const username = stream.creatorName || 'U';
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=7c3aed&color=fff`;
              }}
            />
          )}
          
          {/* Live badge */}
          <div className="absolute top-2 left-2 badge-accent flex items-center space-x-1 z-10">
            <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
            <span>LIVE</span>
          </div>
          
          {/* Token gated badge */}
          {stream.isTokenGated && (
            <div className="absolute top-2 right-2 badge-outline flex items-center space-x-1 z-10">
              <FiLock size={12} />
              <span>Token Gated</span>
            </div>
          )}
          
          {/* Play button overlay */}
          <motion.div 
            className="absolute inset-0 flex items-center justify-center bg-dark-900/50 opacity-0 transition-opacity z-10"
            animate={{ opacity: isHovered ? 1 : 0 }}
          >
            <motion.div 
              className="w-12 h-12 rounded-full bg-primary-600/80 backdrop-blur-sm flex items-center justify-center shadow-3d"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiPlay className="text-white ml-1" size={20} />
            </motion.div>
          </motion.div>
          
          {/* View count */}
          <div className="absolute bottom-2 right-2 badge-primary flex items-center space-x-1 z-10">
            <FiEye size={12} />
            <span>{formatViewCount(stream.viewCount || 0)} watching</span>
          </div>
        </div>
        
        {!compact && (
          <div className="px-2">
            {/* Stream info */}
            <div className="flex items-start space-x-3">
              {/* Creator avatar */}
              <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-dark-700">
                {stream.creatorImageUrl ? (
                  <img 
                    src={stream.creatorImageUrl} 
                    alt={stream.creatorName} 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const username = stream.creatorName || 'U';
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=7c3aed&color=fff`;
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-secondary-600 text-white text-sm font-bold">
                    {stream.creatorName?.charAt(0).toUpperCase() || 'C'}
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-medium text-base mb-1 line-clamp-1">{stream.title}</h3>
                <div className="flex items-center text-gray-400 text-sm">
                  <span className="truncate">{stream.creatorName || 'Unknown Creator'}</span>
                  <span className="mx-1">•</span>
                  <span>{formatTimeAgo(stream.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </Link>
  );
};

// Helper function to format time ago
const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'just now';
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  
  const years = Math.floor(days / 365);
  return `${years}y ago`;
};

export default StreamCard;